// The language of the menu (and later of what the companions say): 'auto', the host's preferred
// languages (a browser's, the system's), or one of engine/i18n.js's LANGUAGES. English is built in;
// any other language is a table of texts the host loads, `load(code)` resolving to `{ key: text }`
// (i18n/<code>.json in the repository). Without a loader, or when a table is missing or broken, it
// is English, with one console warning per language: never an error.
import { LANGUAGES, matchLanguage, setLanguage, language } from '../engine/i18n.js';

export const AUTO = 'auto';
// A choice the option accepts: 'auto' or a language there is.
export const isChoice = value => value === AUTO || (typeof value === 'string' && Object.hasOwn(LANGUAGES, value));

// A table is an object of texts: a string, or for a counted text an object of plural forms.
const isTable = table => !!table && typeof table === 'object' && !Array.isArray(table)
  && Object.values(table).every(text => typeof text === 'string'
    || (!!text && typeof text === 'object' && !Array.isArray(text) && Object.values(text).every(form => typeof form === 'string')));

// `preferred()`: the host's languages, best first (navigator.languages by default). `load(code)`:
// that language's table (a promise; null or a rejection when there is none).
export function createLanguage({ preferred = () => globalThis.navigator?.languages || [], load = null } = {}) {
  let choice = AUTO, latest = 0;
  const warned = new Set();
  const warn = (code, why) => {
    if (warned.has(code)) return;
    warned.add(code);
    console.warn(`Companions: no texts for ${LANGUAGES[code]} (${code}): ${why}; using English.`);
  };
  // What 'auto' picks here.
  const system = () => {
    try { return matchLanguage([...(preferred() || [])]); } catch { return 'en'; }
  };
  async function tableOf(code) {
    if (code === 'en') return {};
    if (!load) { warn(code, 'the host loads no languages'); return null; }
    try {
      const table = await load(code);
      if (isTable(table)) return table;
      warn(code, table == null ? 'its table is missing' : 'its table is broken');
    } catch (e) {
      warn(code, String(e?.message || e));
    }
    return null;
  }
  return {
    get choice() { return choice; },
    system,
    // Uses `next` ('auto' or a code): resolves once its table is in and in use (true), or false
    // when another choice came meanwhile.
    async use(next) {
      choice = isChoice(next) ? next : AUTO;
      const n = ++latest, code = choice === AUTO ? system() : choice;
      const table = await tableOf(code);
      if (n !== latest) return false;
      setLanguage(table ? code : 'en', table || {});
      return true;
    },
    inUse: () => language(),
    reset() { latest++; setLanguage('en'); },
  };
}
